import {
	InfiniteData,
	MailFoldersResponseType,
	useClientAccountOverview,
	UseQueryResult,
} from '@ukpostbox/client-data';
import { AccountOverviewType } from '@ukpostbox/client-data/lib/Types/AccountOverviewTypes';
import {
	Badge,
	Colour,
	ContentBlock,
	ContentLoader,
	SectionNavigation,
	SectionNavigationEntryType,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import React, { ForwardedRef, forwardRef, useCallback, useEffect, useState } from 'react';
import { useMailbox } from '../Providers/NewMailboxProvider';
import MailboxFolders from './MailboxFolders';

export interface MailboxNavigationInterface {
	title: string;
	createFolderSuccessCallback?: any;
	addonContent?: any;
}

const ShapeMenuData = (
	data: InfiniteData<MailFoldersResponseType> | undefined,
	accountOverviewData: UseQueryResult<AccountOverviewType>,
) => {
	const selectOptions: Array<SectionNavigationEntryType> = [];

	if (!data) return selectOptions;

	data.pages.forEach((page) =>
		page.data.forEach((item) => {
			selectOptions.push({
				title: item.name,
				badge:
					item.id === 9 && accountOverviewData?.data?.counts ? (
						<Badge colour={Colour.Alert} value={accountOverviewData?.data?.counts?.lockedMailCount} />
					) : undefined,
				path: `../mail/${item.name.replace(' ', '-').toLowerCase()}`,
				state: item.id === 8 || item.id === 9 ? { includeFolderAssigned: true } : undefined,
			});
		}),
	);

	return selectOptions;
};

const MailboxNavigation = forwardRef((props: MailboxNavigationInterface, ref: ForwardedRef<HTMLDivElement>) => {
	const { t } = useTranslation();
	const { isTiny, isSmall, isMedium } = useLayout();
	const { folderData, folderError, folderStatus } = useMailbox();

	const accountOverviewData = useClientAccountOverview();

	const [mailMenuData, setMailMenuData] = useState<Array<SectionNavigationEntryType>>(
		ShapeMenuData(folderData, accountOverviewData),
	);

	const UpdateMenuData = useCallback(() => {
		if (accountOverviewData && folderData) {
			setMailMenuData(ShapeMenuData(folderData, accountOverviewData));
		}
	}, [folderData, accountOverviewData]);

	useEffect(() => {
		if (folderData && folderStatus === 'success' && mailMenuData.length === 0) {
			UpdateMenuData();
		}
	}, [folderData]);

	useEffect(() => {
		if (accountOverviewData.status === 'success') {
			UpdateMenuData();
		}
	}, [accountOverviewData.dataUpdatedAt]);

	return folderStatus === 'pending' || accountOverviewData.status === 'pending' ? (
		<>
			<div className='p-sm'>
				<ContentLoader childHeight='3em' round childCount={1} />
			</div>
			{!isTiny && !isSmall && !isMedium && (
				<div className='p-sm mt-n-sm'>
					<ContentLoader childHeight='3em' round />
				</div>
			)}
		</>
	) : folderStatus === 'error' ? (
		<ContentBlock error title={folderError?.name as string} content={folderError?.message} extraClass='m-md' />
	) : (
		<SectionNavigation
			ref={ref}
			title={!isTiny && !isSmall && !isMedium ? t('mailboxes.navigation.title') : props.title}
			entries={mailMenuData}
			rounded
			addonContent={props.addonContent}
		>
			<MailboxFolders />
		</SectionNavigation>
	);
});

export default MailboxNavigation;
